/* eslint-disable react/forbid-dom-props */
import { Currency, ProfitabilityEstimateDTO } from 'api';
import { useTranslation } from 'i18n';
import { Box, formatCurrency, formatNumber, Tooltip } from 'ui';
import { HelpOutlineOutlined } from 'ui/icons';

type ResultTableProps = {
  data: ProfitabilityEstimateDTO;
};

const formatAmount = (value: string | undefined | null, currencyCode: Currency = Currency.GBP) => {
  if (!value) {
    return '-';
  }

  return formatCurrency(value, { currencyCode });
};

const formatPercentage = (value: string | undefined | null) => {
  if (!value) {
    return '-';
  }
  return formatNumber(value, {
    minimumFractionDigits: 2,
    style: 'percent',
  });
};

export const ResultTable = (props: ResultTableProps) => {
  const { t } = useTranslation('orders');
  const { data } = props;

  const { revenues, costs, net_revenue, net_take_rate, deal_margin, deal_margin_percentage_of_gmv } = data;

  return (
    <div className="row">
      <div className="col-12 col-md-3 border-end">
        <h4>{t('profitabilityCalculator.resultTable.revenues.title')}</h4>
        <p data-testid="material-revenue">
          {t('profitabilityCalculator.resultTable.revenues.material')}: {formatAmount(revenues.material_revenue_gbp)}
        </p>
        <p data-testid="pern-revenue">
          {t('profitabilityCalculator.resultTable.revenues.pernRevenue')}: {formatAmount(revenues.pern_revenue_gbp)}
          <Box component="span" sx={{ pl: 1 }}>
            <Tooltip title={t('profitabilityCalculator.resultTable.revenues.tooltips.pernRevenue')}>
              <HelpOutlineOutlined />
            </Tooltip>
          </Box>
        </p>
        <p data-testid="gmv" className="text-primary">
          <strong>
            {t('profitabilityCalculator.resultTable.revenues.gmv')}: <span>{formatAmount(revenues.gmv)}</span>
          </strong>
        </p>
      </div>
      <div className="col-12 col-md-3 border-end">
        <h4>{t('profitabilityCalculator.resultTable.costs.title')}</h4>
        <p data-testid="material-cost">
          {t('profitabilityCalculator.resultTable.costs.material')}: {formatAmount(costs.material_cost_gbp)}
        </p>
        <p data-testid="shipping-cost">
          {t('profitabilityCalculator.resultTable.costs.shipping')}: {formatAmount(costs.shipping_cost_gbp)}
        </p>
        <p data-testid="buyer-agent-commission">
          {t('profitabilityCalculator.resultTable.costs.buyerAgentCommission')}:{' '}
          {formatAmount(costs.buyer_agent_commission_gbp)}
        </p>
        <p data-testid="seller-broker-commission">
          {t('profitabilityCalculator.resultTable.costs.sellerAgentCommission')}:{' '}
          {formatAmount(costs.seller_agent_commission_gbp)}
        </p>
        <p data-testid="fx-cost">
          {t('profitabilityCalculator.resultTable.costs.fxCostEstimate')}: {formatAmount(costs.fx_cost)}
          <Box component="span" sx={{ pl: 1 }}>
            <Tooltip title={t('profitabilityCalculator.resultTable.costs.tooltips.fxCostEstimate')}>
              <HelpOutlineOutlined />
            </Tooltip>
          </Box>
        </p>
        <p data-testid="inspections-cost">
          {t('profitabilityCalculator.resultTable.costs.inspectionsEstimate')}: {formatAmount(costs.inspection_cost)}
        </p>
        <p data-testid="insurance-cost">
          {t('profitabilityCalculator.resultTable.costs.insurance')}: {formatAmount(costs.insurance_cost)}
        </p>
        <p data-testid="bank-fees">
          {t('profitabilityCalculator.resultTable.costs.bankFees')}: {formatAmount(costs.bank_fees)}
        </p>
        <p data-testid="financing-cost">
          {t('profitabilityCalculator.resultTable.costs.financingCostEstimate')}: {formatAmount(costs.financing_cost)}
          <Box component="span" sx={{ pl: 1 }}>
            <Tooltip title={t('profitabilityCalculator.resultTable.costs.tooltips.financingCostEstimate')}>
              <HelpOutlineOutlined />
            </Tooltip>
          </Box>
        </p>
        <p data-testid="claims-cost">
          {t('profitabilityCalculator.resultTable.costs.claimsEstimate')}: {formatAmount(costs.claims)}
        </p>
        <p data-testid="logistics-cost">
          {t('profitabilityCalculator.resultTable.costs.logisticCostsEstimate')}: {formatAmount(costs.logistic_cost)}
        </p>
        <p data-testid="unplanned-logistics-cost">
          {t('profitabilityCalculator.resultTable.costs.unplannedLogisticCostsEstimate')}:{' '}
          {formatAmount(costs.unplanned_logistic_costs)}
        </p>
        <p data-testid="total-costs" className="text-primary">
          <strong>
            {t('profitabilityCalculator.resultTable.costs.totalCosts')}:{' '}
            <span>{formatAmount(costs.total_costs_gbp || costs.planned_costs_gbp)}</span>
          </strong>
        </p>
      </div>
      <div className="col-12 col-md-3 border-end">
        <h4>{t('profitabilityCalculator.resultTable.ntr.title')}</h4>
        <p data-testid="net-revenue">
          {t('profitabilityCalculator.resultTable.ntr.netRevenue')}: {formatAmount(net_revenue)}
          <Box component="span" sx={{ pl: 1 }}>
            <Tooltip title={t('profitabilityCalculator.resultTable.ntr.tooltips.netRevenue')}>
              <HelpOutlineOutlined />
            </Tooltip>
          </Box>
        </p>
        <p data-testid="net-revenue-take-rate" className="text-primary">
          <strong>
            {t('profitabilityCalculator.resultTable.ntr.takeRate')}: {formatPercentage(net_take_rate)}
          </strong>
        </p>
      </div>
      <div className="col-12 col-md-3">
        <h4>{t('profitabilityCalculator.resultTable.dealMargin.title')}</h4>
        <p data-testid="deal-margin">
          {t('profitabilityCalculator.resultTable.dealMargin.dealMargin')}: {formatAmount(deal_margin, Currency.GBP)}
          <Box component="span" sx={{ pl: 1 }}>
            <Tooltip title={t('profitabilityCalculator.resultTable.dealMargin.tooltips.dealMargin')}>
              <HelpOutlineOutlined />
            </Tooltip>
          </Box>
        </p>
        <p data-testid="deal-margin-percentage-of-gmv" className="text-primary">
          <strong>
            {t('profitabilityCalculator.resultTable.dealMargin.dealMarginPercentageOfGmv')}:{' '}
            {formatPercentage(deal_margin_percentage_of_gmv)}
          </strong>
        </p>
      </div>
    </div>
  );
};
